import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateActivityMutation } from '../../../../hooks/mutations/useActivityMutation';
import { useActivitiesTypesQuery } from '../../../../hooks/queries/useActivitiesTypesQuery';
import { useSitesQuery } from '../../../../hooks/queries/useSitesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { activityStateEnum, activityTypeEnum, permissionLevels, reportStateEnum, reportTypeEnum, stepModes } from '../../../../utils/enums';
import DropDown from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { isSameDayOrAfter } from '../../../../utils/dates';
import CustomTextInput from '../../../CustomTextInput';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem } from '../../../../services/apiSLTP/activities';
import CustomTimePicker from '../../../CustomTimePicker';

interface CreateActivityNeedsModalProps extends ModalProps {
    initDate: string;
    refetch?: () => void;
    initUserId?: string;
    initSiteId?: string;
    initActivityTypeId?: string;
    currentDate: Date;
    stepMode?: number;
    reportState?: number;
    activityState?: number;
    showSpecialUsers?: boolean;
    activities?: DataItem[];
}

const CreateActivityNeedsModal: React.FC<CreateActivityNeedsModalProps> = ({
    open,
    handleClose,
    initDate,
    refetch = () => {},
    initUserId = '',
    initSiteId = '',
    initActivityTypeId = '',
    currentDate,
    stepMode = stepModes.PLANNING,
    reportState = reportStateEnum.PLANNING,
    activityState = activityStateEnum.PLANNING,
    showSpecialUsers = true,
    activities,
}) => {
    const [selectedUser, setSelectedUser] = useState({ label: '', value: '' });
    const [selectedActivityType, setSelectedActivityType] = useState({ label: '', value: '' });
    const [selectedSite, setSelectedSite] = useState({ label: '', value: '' });
    const [selectedDate, setSelectedDate] = useState(initDate);
    const { t } = useTranslation();
    const { user } = useAuth();

    const moTruckId = '66b097d4b0a672302822c715';
    const moAmplirollId = '66e14538c7639ac65e2e6763';

    const { data: dataSites, isLoading: isSitesLoading } = useSitesQuery({
        queries: { active: true, conducId: user?._id, assistantConducId: user?._id },
    });
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { data: dataActivitiesTypes, isLoading: isActivitiesTypesLoading } = useActivitiesTypesQuery({ queries: { active: true } });
    const {
        data: dataReports,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const { mutate: createActivity } = useCreateActivityMutation({
        onSuccess: (data) => {
            refetch();
            setSelectedUser({ label: '', value: '' });
            setSelectedActivityType({ label: '', value: '' });
            handleClose();
        },
    });

    const handleCreateActivity = () => {
        createActivity({
            activityTypeId: selectedActivityType.value,
            siteId: selectedSite.value,
            startTime: selectedDate,
            userId: selectedUser.value,
            activityState,
            reportState,
            needsValidationTime: new Date().toISOString(),
            stepMode: stepMode,
        });
    };

    const checkSiteState = (siteId: string): number => {
        let tmpState = 0;
        const tmpActivities = activities?.filter((value) => value.siteId._id === siteId);
        if (!tmpActivities || tmpActivities.length === 0) return activityStateEnum.NEEDS;
        for (let i = 0; i < tmpActivities.length; i++) {
            if (tmpActivities[i].activityState > tmpState) tmpState = tmpActivities[i].activityState;
        }
        return tmpState;
    };

    const handleChangePerson = (label: string, value: string) => {
        setSelectedUser({ value: value, label: label });
        const tmpUser = dataUsers?.find((user) => user._id === value);
        if (tmpUser) {
            if (tmpUser.profileId.permsLevel === permissionLevels.CHAUFFEUR)
                setSelectedActivityType({
                    label: dataActivitiesTypes?.find((value) => value.type === activityTypeEnum.TRUCK)?.designation || '',
                    value: dataActivitiesTypes?.find((value) => value.type === activityTypeEnum.TRUCK)?._id || '',
                });
            else setSelectedActivityType({ label: '', value: '' });
        }
    };

    useEffect(() => {
        if (initUserId.length) {
            const tmpUser = dataUsers?.find((user) => user._id === initUserId);
            setSelectedUser({
                label: tmpUser ? formatName(tmpUser.firstName, tmpUser.lastName) : '',
                value: initUserId,
            });
        }
    }, [dataUsers]);

    useEffect(() => {
        if (initSiteId.length) {
            const tmpSite = dataSites?.find((site) => site._id === initSiteId);
            setSelectedSite({
                label: tmpSite ? tmpSite.name : '',
                value: initSiteId,
            });
        }
    }, [dataSites]);

    useEffect(() => {
        if (initActivityTypeId.length) {
            const tmpActivityType = dataActivitiesTypes?.find((activityType) => activityType._id === initActivityTypeId);
            setSelectedActivityType({
                label: tmpActivityType ? tmpActivityType.designation : '',
                value: initActivityTypeId,
            });
        }
    }, [dataActivitiesTypes]);

    return (
        <ModalBody
            closeText={t('cancel')}
            okText={t('validate')}
            title={t('activity')}
            subTitle={t('add')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                handleCreateActivity();
            }}
            disabledOk={!selectedUser.value.length || !selectedSite.value.length || !selectedActivityType.value.length}
        >
            <DropDown
                selectedItem={selectedSite}
                items={
                    dataSites
                        ?.filter((site) => checkSiteState(site._id) >= activityStateEnum.NEEDSFILLED)
                        ?.map((site) => ({ label: site.name, value: site._id })) || []
                }
                handleChange={(event, value) => setSelectedSite({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('site')}
                loading={isSitesLoading}
                required
            />
            <DropDown
                selectedItem={selectedUser}
                items={
                    dataUsers
                        ?.filter(
                            (user) =>
                                dataReports?.find((report) => report.userId._id === user._id)?.reportType !== reportTypeEnum.ABSENT &&
                                user.profileId.permsLevel >= permissionLevels.TERRAIN &&
                                (!user.arrivalDate || (user.arrivalDate && isSameDayOrAfter(currentDate, new Date(user.arrivalDate)))) &&
                                (showSpecialUsers || !user.isSpecial)
                        )
                        ?.map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user._id,
                        })) || []
                }
                handleChange={(event, value) => handleChangePerson(value?.label || '', value?.value || '')}
                inputLabel={t('person')}
                loading={isUsersLoading}
                required
            />
            <DropDown
                selectedItem={selectedActivityType}
                items={
                    dataActivitiesTypes
                        ?.filter((activityType) =>
                            [moAmplirollId, moTruckId].includes(selectedUser.value)
                                ? activityType.type === activityTypeEnum.TRUCK
                                : activityType.type !== activityTypeEnum.TRUCK
                        )
                        .map((activityType) => ({ label: activityType.designation, value: activityType._id })) || []
                }
                handleChange={(event, value) => setSelectedActivityType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activity')}
                loading={isActivitiesTypesLoading}
                required
            />
            <CustomTimePicker
                label={capitalizeFirstLetter(t('startTime'))}
                value={selectedDate}
                onChange={(isoString) => {
                    setSelectedDate(isoString);
                }}
                required={true}
            />
        </ModalBody>
    );
};

export default CreateActivityNeedsModal;
